import type { FilePublicWithPermissions } from '@repo/types/files';
import type { MouseEvent, PointerEvent, ReactNode } from 'react';

import { cn } from '@repo/lib/cn';
import { FileTypeUtils } from '@repo/lib/fs/fileTypeUtils';
import { FileType } from '@repo/types/enums/fileType';
import { formatDistanceToNow } from 'date-fns';
import { ArrowLeftIcon, ArrowRightIcon, DownloadIcon, InfoIcon, LinkIcon } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';

import { InfoFsEntryContent } from '@/components/features/files/forms/InfoFSEntry';
import { MediaAudioViewerContent } from '@/components/features/media/MediaAudioViewer';
import { MediaFileViewerContent } from '@/components/features/media/MediaFileViewer';
import { MediaImageViewerContent } from '@/components/features/media/MediaImageViewer';
import { MediaPDFViewerContent, MediaPDFViewerNavigation, MediaPDFViewerNavigationResize, MediaPdfViewerProvider } from '@/components/features/media/MediaPDFViewer';
import { MediaVideoViewerContent } from '@/components/features/media/MediaVideoViewer';
import { Button } from '@/components/ui/button';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FullscreenRoot, FullscreenTarget } from '@/components/ui/fullscreen';
import { useCopyToClipboard } from '@/hooks/copy-to-clipboard';
import { useMediaQuery } from '@/hooks/media-query';
import { useFilesStorage } from '@/store/files-store';
import { getFileUrl } from '@/utils/file/getFileUrl';

interface FsMediaViewerProps {
  /**
   * A flat, ordered array of files available in the current directory view.
   * The order of this array determines how the navigation arrows iterate over files.
   */
  files?: FilePublicWithPermissions[];
}

/**
 * Media viewer dialog with navigation controls for browsing files.
 *
 * Features:
 * - Supports multiple file types (PDF, images, video, audio, generic files)
 * - Keyboard navigation (arrow keys for prev/next, Delete/Backspace to delete)
 * - Fullscreen mode
 * - Download functionality
 * - Responsive design with different controls for mobile/desktop
 */
export default function FsMediaViewer(fsMediaViewerProps: FsMediaViewerProps) {
  const { files = [] } = fsMediaViewerProps;

  const isDesktop = useMediaQuery('lg');
  const [showInfo, setShowInfo] = useState<boolean>(false);

  const { fileToOpen, openViewer, setFileToOpen, setOpenViewer, setSelected, setOpenDelete } = useFilesStorage();

  // Navigation state
  const { currentIndex, hasPrev, hasNext } = useFileNavigation(files, fileToOpen);

  // File metadata
  const { fileUrl, serveType, subtype, isFilesInstance, isPdf } = useFileMetadata(fileToOpen);

  // Permissions
  const canDelete = Boolean(fileToOpen?.permissions?.write);

  // Navigation handlers
  const goToIndex = useCallback(
    (nextIndex: number): void => {
      const target = files[nextIndex];
      if (target) {
        setFileToOpen(target);
      }
    },
    [files, setFileToOpen]
  );

  const handlePrev = useCallback((): void => {
    if (hasPrev) {
      goToIndex(currentIndex - 1);
    }
  }, [currentIndex, goToIndex, hasPrev]);

  const handleNext = useCallback((): void => {
    if (hasNext) {
      goToIndex(currentIndex + 1);
    }
  }, [currentIndex, goToIndex, hasNext]);

  const handleDelete = useCallback(() => {
    if (!fileToOpen || !canDelete) return;

    setSelected({ files: [fileToOpen], directories: [] });
    setOpenViewer(false);
    setOpenDelete(true);
  }, [fileToOpen, canDelete, setOpenDelete, setOpenViewer, setSelected]);

  // Keyboard navigation
  useKeyboardNavigation(openViewer, handlePrev, handleNext, handleDelete, canDelete);

  useEffect(() => {
    if (!openViewer) {
      setShowInfo(false);
    }
  }, [openViewer, setSelected]);

  // Derived state
  const showNavigationControls = openViewer && fileToOpen && files.length > 1;

  if (!openViewer) {
    return null;
  }

  return (
    <>
      {fileToOpen ? (
        <MediaPdfViewerProvider file={fileToOpen}>
          <Dialog open={openViewer} onOpenChange={setOpenViewer}>
            <FullscreenRoot>
              <FullscreenTarget asChild fullscreenClassName="fixed inset-0 w-screen h-screen max-w-none max-h-none rounded-none shadow-none top-0 left-0 translate-x-0 translate-y-0">
                <DialogContent className={'h-full max-h-224'}>
                  <DialogBody>
                    <DialogHeader className="flex flex-col border-b">
                      <div className="space-y-0.5">
                        <DialogTitle>{fileToOpen?.name}</DialogTitle>
                        <DialogDescription>
                          {fileToOpen?.updatedAt &&
                            `Zmodyfikowano ${formatDistanceToNow(fileToOpen.updatedAt, {
                              includeSeconds: false,
                              addSuffix: true,
                            })}`}
                        </DialogDescription>
                      </div>
                      {isPdf && <PdfNavigation />}
                    </DialogHeader>
                    <div className={'relative flex min-h-0 flex-1 flex-col overflow-hidden md:flex-row'}>
                      <div className={cn('flex w-full justify-center overflow-y-auto transition-all duration-300 ease-in-out', showInfo ? 'h-40 grow-0' : 'grow', 'md:h-full md:grow')}>
                        <ViewerContent file={fileToOpen} fileUrl={fileUrl} serveType={serveType} subtype={subtype} isFilesInstance={isFilesInstance} />
                      </div>
                      <div
                        className={cn(
                          'bg-background overflow-x-hidden overflow-y-auto transition-all duration-300 ease-in-out',
                          showInfo ? 'grow border-t opacity-100' : 'h-0 border-none opacity-0',
                          'md:h-full md:border-t-0',
                          showInfo ? 'md:w-sm md:min-w-sm md:border-l md:opacity-100' : 'md:w-0 md:min-w-0 md:border-none md:opacity-0'
                        )}
                      >
                        <div className={'w-full md:w-sm'}>
                          <InfoFsEntryContent element={fileToOpen} />
                        </div>
                      </div>
                    </div>
                  </DialogBody>
                  <ViewerFooter fileUrl={fileUrl} fileId={fileToOpen.id} showDownload={isFilesInstance} showInfo={showInfo} onToggleInfo={() => setShowInfo(!showInfo)} />
                </DialogContent>
              </FullscreenTarget>
            </FullscreenRoot>
          </Dialog>
        </MediaPdfViewerProvider>
      ) : (
        <Dialog open={openViewer} onOpenChange={setOpenViewer}>
          <DialogContent>
            <DialogBody>
              <DialogHeader className="flex flex-col border-b">
                <DialogTitle>No file selected</DialogTitle>
              </DialogHeader>
            </DialogBody>
          </DialogContent>
        </Dialog>
      )}

      {showNavigationControls && fileToOpen && (
        <NavigationPortal>
          <NavigationControls hasPrev={hasPrev} hasNext={hasNext} onPrev={handlePrev} onNext={handleNext} isDesktop={isDesktop} />
        </NavigationPortal>
      )}
    </>
  );
}

interface FileNavigationState {
  currentIndex: number;
  hasPrev: boolean;
  hasNext: boolean;
}

/**
 * Hook to manage file navigation state within the ordered file list.
 */
function useFileNavigation(files: FilePublicWithPermissions[], currentFile: FilePublicWithPermissions | undefined): FileNavigationState {
  const currentIndex = useMemo<number>(() => {
    if (!currentFile) return -1;
    return files.findIndex((entry) => entry.id === currentFile.id);
  }, [files, currentFile]);

  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex !== -1 && currentIndex < files.length - 1;

  return { currentIndex, hasPrev, hasNext };
}

/**
 * Hook to handle keyboard navigation within the media viewer.
 */
function useKeyboardNavigation(isActive: boolean, onPrev: () => void, onNext: () => void, onDelete: () => void, canDelete: boolean): void {
  useEffect(() => {
    if (!isActive) return;

    const handleKeyDown = (event: KeyboardEvent): void => {
      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          onPrev();
          break;
        case 'ArrowRight':
          event.preventDefault();
          onNext();
          break;
        case 'Delete':
        case 'Backspace':
          if (canDelete) {
            event.preventDefault();
            onDelete();
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isActive, onPrev, onNext, onDelete, canDelete]);
}

/**
 * Hook to derive file metadata from the current file.
 */
export function useFileMetadata(file: FilePublicWithPermissions | undefined) {
  const fileUrl = useMemo(() => {
    if (file && 'id' in file) {
      return getFileUrl(file.id);
    }
    return '';
  }, [file]);

  const { serveType, subtype } = useMemo(() => {
    if (!file) {
      return { serveType: FileType.FILE, subtype: '' };
    }
    const result = FileTypeUtils.getFileType(file);
    return {
      serveType: result.serveType,
      subtype: result.subtype ?? '',
    };
  }, [file]);

  const isFilesInstance = Boolean(file && 'id' in file);
  const isPdf = subtype === 'pdf';

  return { fileUrl, serveType, subtype, isFilesInstance, isPdf };
}

interface NavigationControlsProps {
  hasPrev: boolean;
  hasNext: boolean;
  onPrev: () => void;
  onNext: () => void;
  isDesktop: boolean;
}

/**
 * Navigation arrow buttons for prev/next file navigation.
 */
function NavigationControls(navigationControlsProps: NavigationControlsProps) {
  const { hasPrev, hasNext, onPrev, onNext, isDesktop } = navigationControlsProps;

  const handlePointerDown = useCallback((event: MouseEvent | PointerEvent) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const buttonSize = isDesktop ? 'md' : 'sm';

  return (
    <div className={'pointer-events-none fixed inset-0 z-70 select-none'}>
      <div className={'pointer-events-auto absolute top-1/2 left-1 -translate-y-1/2 lg:left-4'}>
        <Button size={buttonSize} variant={'secondary'} className={'shadow-lg'} onPointerDown={handlePointerDown} onClick={onPrev} disabled={!hasPrev} aria-label={'View previous file'}>
          <ArrowLeftIcon />
        </Button>
      </div>

      <div className={'pointer-events-auto absolute top-1/2 right-1 -translate-y-1/2 lg:right-4'}>
        <Button size={buttonSize} variant={'secondary'} className={'shadow-lg'} onPointerDown={handlePointerDown} onClick={onNext} disabled={!hasNext} aria-label={'View next file'}>
          <ArrowRightIcon />
        </Button>
      </div>
    </div>
  );
}

interface ViewerContentProps {
  file: FilePublicWithPermissions;
  fileUrl: string;
  serveType: FileType;
  subtype: string;
  isFilesInstance: boolean;
}

/**
 * Renders the appropriate media viewer content based on file type.
 */
export function ViewerContent(viewerContentProps: ViewerContentProps) {
  const { file, fileUrl, serveType, subtype, isFilesInstance } = viewerContentProps;

  if (subtype === 'pdf') {
    return <MediaPDFViewerContent />;
  }

  if (serveType === FileType.IMAGE) {
    return <MediaImageViewerContent src={fileUrl} alt={file.name} blurDataURL={isFilesInstance && file.thumbnail ? file.thumbnail : undefined} type={isFilesInstance ? file.fileType : ''} />;
  }

  if (serveType === FileType.VIDEO) {
    return <MediaVideoViewerContent src={fileUrl} />;
  }

  if (serveType === FileType.AUDIO) {
    return (
      <div className={'flex w-full grow items-center p-10 sm:p-14'}>
        <MediaAudioViewerContent src={fileUrl} />
      </div>
    );
  }

  return <MediaFileViewerContent fileName={file.name} src={fileUrl} isFileObject={false} />;
}

/**
 * PDF-specific navigation controls shown in the header.
 */
function PdfNavigation() {
  return (
    <div className={'flex justify-between'}>
      <MediaPDFViewerNavigation />
      <MediaPDFViewerNavigationResize />
    </div>
  );
}

interface ViewerFooterProps {
  fileUrl: string;
  fileId: string;
  showDownload: boolean;
  showInfo: boolean;
  onToggleInfo: () => void;
}

/**
 * Footer controls including fullscreen toggle and download button.
 */
function ViewerFooter(viewerFooterProps: ViewerFooterProps) {
  const { fileUrl, fileId, showDownload, showInfo, onToggleInfo } = viewerFooterProps;
  const [, copy] = useCopyToClipboard();

  return (
    <DialogFooter className={'bg-accent dark:bg-card space-x-1 border-t'}>
      {showDownload && (
        <a href={fileUrl} download target="_self">
          <Button variant="ghost" size="sm" aria-label="Download file">
            <DownloadIcon />
          </Button>
        </a>
      )}
      <Button
        variant="ghost"
        size="sm"
        aria-label="Kopiuj link"
        onClick={() => {
          void copy(`${window.location.origin}/fs/file/${fileId}`)
            .then(() => toast.success('Link skopiowany do schowka'))
            .catch(() => toast.error('Nie udało się skopiować linku'));
        }}
      >
        <LinkIcon />
      </Button>
      <Button variant={showInfo ? 'tertiary' : 'ghost'} size="sm" onClick={onToggleInfo} aria-label={showInfo ? 'Hide info' : 'Show info'}>
        <InfoIcon />
      </Button>
    </DialogFooter>
  );
}

interface NavigationPortalProps {
  children: ReactNode;
}
/**
 * Portal wrapper that safely renders content to document body.
 */
function NavigationPortal(navigationPortalProps: NavigationPortalProps) {
  const { children } = navigationPortalProps;
  if (typeof document === 'undefined') {
    return <>{children}</>;
  }
  return createPortal(children, document.body);
}
